/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

export interface TrailingSplit {
	/** Text rendered as normal, wrapping copy (keeps the separating space, if any). */
	leading: string;
	/** Text glued to the chevron icon so the icon never orphans onto its own line. */
	trailing: string;
}

/**
 * Longest last word we keep glued to the chevron. Beyond this the trailing word is itself
 * wide enough to overflow as a non-breaking run, so we fall back to gluing only the last
 * character instead.
 */
const MAX_TRAILING_WORD_LENGTH = 10;

/**
 * Splits title text into a leading part and a trailing part so the trailing part
 * can be rendered together with the chevron icon, keeping the icon from orphaning
 * onto its own line.
 *
 * - Space-separated copy → trailing is the last word; leading keeps the separating space.
 * - A single long token, a script without spaces (e.g. Japanese/Chinese), or a last word
 *   longer than {@link MAX_TRAILING_WORD_LENGTH} → trailing is the last character, so an
 *   overly wide token isn't glued into the non-breaking inline-flex run.
 *
 * Splitting is code-point safe (surrogate pairs / emoji are not torn apart).
 */
export function splitTrailingSegment(text: string | null | undefined): TrailingSplit {
	if (!text) {
		return { leading: "", trailing: "" };
	}

	const lastSpaceIndex = text.lastIndexOf(" ");
	if (lastSpaceIndex >= 0) {
		const lastWord = text.slice(lastSpaceIndex + 1);
		if (Array.from(lastWord).length <= MAX_TRAILING_WORD_LENGTH) {
			return { leading: text.slice(0, lastSpaceIndex + 1), trailing: lastWord };
		}
	}

	const chars = Array.from(text);
	return { leading: chars.slice(0, -1).join(""), trailing: chars.at(-1) ?? "" };
}
